export type DocsArea = 'consola' | 'super-admin';

export type ResolvedConsoleHelp = {
  area: DocsArea;
  slug: string;
  /** Título para tooltip y accesibilidad */
  title: string;
};

/** Solo si el usuario quiere el sitio público de docs aunque esté en `vite dev`. */
function devWantsPublicDocsSite(): boolean {
  const v = import.meta.env.VITE_DOCS_PUBLIC_DOCS;
  return v === '1' || v === 'true';
}

function explicitDocsBase(): string | null {
  const raw = import.meta.env.VITE_DOCS_BASE_URL;
  if (typeof raw === 'string' && raw.trim()) return raw.replace(/\/+$/, '');
  return null;
}

function isLoopbackHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}

/**
 * Sirve Markdown crudo bajo `/local-docs` (middleware de Vite). La UI usa sobre todo `/app/docs`.
 */
export function preferLocalMarkdownDocs(): boolean {
  if (import.meta.env.DEV && !devWantsPublicDocsSite()) return true;
  if (typeof window !== 'undefined' && isLoopbackHostname(window.location.hostname)) return true;
  return false;
}

/** Inicio del visor de ayuda dentro de la consola. */
export function getDocsBaseUrl(): string {
  return '/app/docs';
}

/** Artículo en el visor embebido (SPA). */
export function buildInAppDocsArticleHref(area: DocsArea, slug: string): string {
  const clean = slug.replace(/^\/+|\/+$/g, '');
  return `/app/docs/${area}/${clean}`;
}

/** Artículo en el visor público de docs (`/docs/...`), p. ej. super admin o enlaces publicados. */
export function buildPublicDocsArticleHref(area: DocsArea, slug: string): string {
  const clean = slug.replace(/^\/+|\/+$/g, '');
  return `/docs/${area}/${clean}`;
}

/**
 * Enlace contextual: consola autenticada → `/app/docs/...`; super admin → `/docs/...` (nueva pestaña).
 */
export function buildHelpDocsPath(pathname: string, help: ResolvedConsoleHelp): string {
  if (pathname.startsWith('/super')) {
    return buildPublicDocsArticleHref(help.area, help.slug);
  }
  return buildInAppDocsArticleHref(help.area, help.slug);
}

/**
 * URL “publicada” (sitio externo o, en override, base explícita). Para enlaces en la app preferí `buildInAppDocsArticleHref`.
 */
export function buildPublishedDocUrl(area: DocsArea, slug: string): string {
  if (devWantsPublicDocsSite() || explicitDocsBase()) {
    const clean = slug.replace(/^\/+|\/+$/g, '');
    const ex = explicitDocsBase();
    if (ex) return `${ex}/${area}/${clean}`;
    return `https://docs.virtual-agents.app/${area}/${clean}`;
  }
  return buildInAppDocsArticleHref(area, slug);
}
